import { ClearOutlined, FormOutlined, InfoCircleFilled } from '@ant-design/icons';
import styled from '@emotion/styled';
import { Button, message, Tooltip } from 'antd';
import StatusButton from 'components/StatusButton';
import TemplateEditor from 'components/TemplateEditor';
import {
  Concept,
  getConceptOccupiedLength,
  getSegmentsLength,
} from 'components/TemplateEditor/Editor';
import conceptPlugin from 'components/TemplateEditor/lySlatePlugins/conceptPlugin';
import dividingLinePlugin from 'components/TemplateEditor/lySlatePlugins/dividingLinePlugin';
import { mustache2SlateValue } from 'components/TemplateEditor/mustacheUtils';
import ConceptMenu from 'components/TemplateEditor/toolbarPlugins/ConceptMenu';
import DividingLineButton from 'components/TemplateEditor/toolbarPlugins/DividingLineButton';
import { CustomSlateNodeType } from 'components/TemplateEditor/types';
import { HTMLAttributes, useState, useRef, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useTranslate } from '@tolgee/react';
import { Descendant, Text } from 'slate';
import { translateTemplate } from 'views/Aide/Scenario/PolicyForm/TranslateTool/utils';
import { useRegionLanguageMap } from 'hooks/business/system/useRegionLanguage';
import { RegionLangSelect } from 'components/Langs/RegionLangSelect';
import WorldLangSelect from 'components/Langs/WorldLangSelect';

const Container = styled.div`
  border: solid 1px var(--fett-border-default);
  border-radius: 2px;
`;

const Toolbar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 48px;
  padding: 0 8px;
  border-bottom: solid 1px var(--fett-border-default);
  > div {
    display: flex;
    align-items: center;
  }
`;

const Title = styled.div`
  height: 28px;
  line-height: 28px;
  padding: 0 8px;
  background: #d7e1fa;
  color: var(--fett-scenario-accent-text);
  border-radius: 2px;
`;

const ContentWrapper = styled.div`
  display: flex;
  height: 240px;
`;

const Content = styled.div<{ disabled?: string; attachment?: string }>`
  flex: 1 0 0;
  position: relative;
  > section > div {
    border-radius: 0;
    border: none;
    padding: 30px 12px 40px 12px;
    height: ${({ attachment }) => (attachment === 'true' ? '272px' : '240px')} !important;
    background: ${({ disabled }) => (disabled === 'true' ? '#f5f5f5' : 'var(--fett-bg-panel)')};
    cursor: ${({ disabled }) => (disabled === 'true' ? 'not-allowed' : 'auto')};
    > div {
      height: 165px;
      padding: 0;
    }
  }
`;

const TranslatedContent = styled(Content)`
  border-left: solid 1px var(--fett-border-default);
`;

const Tips = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  height: 26px;
  padding: 0 10px 0 6px;
  line-height: 26px;
  color: rgba(0, 0, 0, 0.65);
  background-color: #faedd2;
  font-size: 12px;
  z-index: 1;
  svg {
    color: var(--fett-surface-warning-text);
    margin-right: 6px;
  }
`;

const FootBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  position: absolute;
  bottom: 0;
  width: 100%;
  padding: 2px 12px 8px;
`;

const Attachment = styled.div`
  position: absolute;
  left: 12px;
  bottom: 32px;
`;

const Translate = styled.div`
  display: flex;
  align-items: center;
  > span {
    white-space: nowrap;
  }
  .ant-select-selector {
    margin-top: 2px;
    padding-left: 0 !important;
    border: none !important;
  }
`;

export const Hint = styled.div<{ error: string }>`
  font-size: 12px;
  color: ${({ error }) =>
    error === 'true' ? 'var(--ant-error-color)' : 'var(--fett-text-tertiary)'};
`;

export const getTextLength = (segments: Descendant[], initialLength = 0): number => {
  return segments.reduce((res, segment) => {
    if (Text.isText(segment)) {
      return res + segment.text.length;
    }

    if (segment.type === CustomSlateNodeType.CONCEPT) {
      return res + getConceptOccupiedLength(segment);
    }

    if (segment.type === CustomSlateNodeType.PARAGRAPH) {
      return getTextLength(segment.children, res);
    }

    return res;
  }, initialLength);
};

export interface ContextEditorProps extends HTMLAttributes<HTMLDivElement> {
  context: AutoEmit.ExecuteContext<Descendant[]>;
  onContextChange: (context: AutoEmit.ExecuteContext<Descendant[]>) => void;
  limit: number;
  platform?: General.PlatformUpperCase;
  region?: General.UpperCaseRegion;
  title?: string;
  concepts?: Concept[];
  allowDividing?: boolean;
  extra?: ReactNode;
  leftExtra?: ReactNode;
  attachment?: ReactNode;
  showToolbar?: boolean;
  supportTranslate?: boolean;
  disabled?: boolean;
  showTips?: boolean;
}

function ContextEditor(props: ContextEditorProps) {
  const {
    context: { content, translatedContent },
    onContextChange,
    limit,
    platform,
    region,
    title,
    concepts,
    allowDividing = false,
    extra = null,
    leftExtra,
    attachment,
    showToolbar = true,
    supportTranslate = false,
    disabled,
    showTips = true,
    ...otherProps
  } = props;

  const plugins = [conceptPlugin, dividingLinePlugin];

  const { t } = useTranslate();

  const [translationEditable, setTranslationEditable] = useState(false);

  const conceptContainerRef = useRef<HTMLDivElement>(null);

  const tips = showTips && (
    <Tips>
      <InfoCircleFilled />
      {t('phrase.最终发送内容')}
    </Tips>
  );

  const contentLength = getSegmentsLength(content);
  const translatedContentLength = supportTranslate ? getSegmentsLength(translatedContent) : 0;

  const translateBtnClicked = useRef(false);

  const regionLanguageMap = useRegionLanguageMap();
  const [translateLanguage, setTranslateLanguage] = useState(
    platform === 'FACEBOOK' ? regionLanguageMap.FACEBOOK : region && regionLanguageMap[region],
  );

  return (
    <Container {...otherProps}>
      {showToolbar && (title || extra || concepts?.length || allowDividing || leftExtra) && (
        <Toolbar>
          <div>
            {title ? <Title>{title}</Title> : null}
            <div ref={conceptContainerRef} />
          </div>
          <div>{extra}</div>
        </Toolbar>
      )}
      <ContentWrapper style={attachment ? { height: '272px' } : undefined}>
        <Content disabled={String(Boolean(!region))} attachment={String(Boolean(attachment))}>
          {translatedContentLength === 0 && tips}
          <section>
            <TemplateEditor
              placeholder={t('phrase.请输入回复内容')}
              lySlatePlugins={plugins}
              toolbarChildren={false}
              value={content}
              readOnly={!region}
              onChange={(content) => {
                onContextChange({
                  content,
                  translatedContent,
                });
              }}
            >
              {showToolbar && (concepts?.length || allowDividing || leftExtra)
                ? createPortal(
                    <>
                      {concepts && concepts.length > 0 ? (
                        <ConceptMenu disabled={!region || disabled} concepts={concepts} />
                      ) : null}
                      {allowDividing && <DividingLineButton disabled={disabled} />}
                      {leftExtra}
                    </>,
                    conceptContainerRef.current || document.body,
                  )
                : null}
            </TemplateEditor>
          </section>
          <Attachment>{attachment}</Attachment>
          <FootBar>
            <Hint error={String(contentLength > limit)}>{`${contentLength}/${limit}`}</Hint>
            {supportTranslate && region && (
              <Translate>
                <span>{t('翻译为')}：</span>
                {platform === 'FACEBOOK' || platform === 'JOLECT' ? (
                  <WorldLangSelect
                    disabled={disabled}
                    bordered={false}
                    value={translateLanguage}
                    onChange={setTranslateLanguage}
                  />
                ) : (
                  <RegionLangSelect
                    disabled={disabled}
                    bordered={false}
                    region={region}
                    value={translateLanguage}
                    onChange={setTranslateLanguage}
                  />
                )}
                <StatusButton
                  className="ml-[4px]"
                  type="primary"
                  disabled={!region || !contentLength || disabled}
                  onClick={() => {
                    translateBtnClicked.current = true;

                    if (!translateLanguage) {
                      return Promise.resolve();
                    }

                    return translateTemplate(content, translateLanguage, [], 'GOOGLE', {
                      from: 'translateTool',
                      platform,
                      region,
                    })
                      .then((translatedContent) => {
                        onContextChange({
                          content,
                          translatedContent,
                        });
                      })
                      .catch(() => {
                        message.error(t('phrase.翻译失败'));
                      });
                  }}
                >
                  {t('phrase.翻译')}
                </StatusButton>
              </Translate>
            )}
          </FootBar>
        </Content>
        {supportTranslate && (
          <TranslatedContent
            disabled={String(!translationEditable)}
            attachment={String(Boolean(attachment))}
            style={{
              display: `${
                translatedContentLength > 0 || translateBtnClicked.current ? 'block' : 'none'
              }`,
            }}
          >
            {translatedContentLength > 0 && tips}
            <section>
              <TemplateEditor
                lySlatePlugins={plugins}
                value={translatedContent}
                toolbarChildren={false}
                readOnly={!translationEditable}
                onChange={(translatedContent) =>
                  onContextChange({
                    content,
                    translatedContent,
                  })
                }
                onBlur={() => setTranslationEditable(false)}
              />
            </section>
            <FootBar>
              <Hint error={String(translatedContentLength > limit)}>
                {`${translatedContentLength}/${limit}`}
              </Hint>
              {translatedContentLength > 0 && (
                <div>
                  {translationEditable ? (
                    <Tooltip title={t('department.清空')}>
                      <Button
                        type="text"
                        disabled={disabled}
                        onClick={() =>
                          onContextChange({
                            content,
                            translatedContent: mustache2SlateValue(''),
                          })
                        }
                      >
                        <ClearOutlined />
                      </Button>
                    </Tooltip>
                  ) : (
                    <Tooltip title={t('aide.编辑')}>
                      <Button
                        disabled={disabled}
                        type="text"
                        onClick={() => setTranslationEditable(true)}
                      >
                        <FormOutlined />
                      </Button>
                    </Tooltip>
                  )}
                </div>
              )}
            </FootBar>
          </TranslatedContent>
        )}
      </ContentWrapper>
    </Container>
  );
}

export default ContextEditor;
